<script setup>
import { ref, computed, onMounted } from 'vue'
import _ from 'lodash'
import moment from 'moment'
import PaywallService from "@/services/PaywallService";
import { useNotification } from "@kyvg/vue3-notification";
import FormInput from '../Atoms/FormInput.vue';
import FormSelect from '../Atoms/FormSelect.vue';
import FormSwitch from '../Atoms/FormSwitch.vue';
import FormButton from '../Atoms/FormButton.vue';
import IconBase from '../icon/IconBase.vue';
import IconBin from '../icon/IconBin.vue';
import IconEdit from '../icon/IconEdit.vue';
import IconDownload from '../icon/IconDownload.vue';
import Spinner from '../ui/Spinner.vue';
import InlineDateTimePicker from '../Atoms/InlineDateTimePicker.vue';

const { notify } = useNotification()

const props = defineProps({
  currentPaywall: {
    type: [Object, Function],
  }
})

const loading = ref(true)
const codes = ref([])
const showForm = ref(false)
const saving = ref(false)

// Per-code redemption lists (who used each code)
const expandedId = ref(null)
const redemptionsByCode = ref({})
const loadingRedemptions = ref(null)

// New-code form state
const form = ref({
  code: '',
  type: 'percent',
  value: '',
  currency: (props.currentPaywall?.currency || 'usd').toLowerCase(),
  max_redemptions: '',
  expires_at: null,
})

const typeOptions = [
  { value: 'percent', text: 'Percentage off' },
  { value: 'fixed', text: 'Fixed amount off' },
  { value: 'comp', text: 'Complimentary access' },
]

// Compare public_ids ignoring the toggle "_disabled" suffix
const streamPublicId = computed(() => String(props.currentPaywall?.public_id || '').replace('_disabled', ''))

const belongsToStream = (c) => String(c.stream || '').replace('_disabled', '') === streamPublicId.value

const loadCodes = async () => {
  loading.value = true
  try {
    const all = await PaywallService.getPromoCodes()
    codes.value = _.filter(all || [], belongsToStream)
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't load promo codes" })
    codes.value = []
  } finally {
    loading.value = false
  }
}

onMounted(loadCodes)

const resetForm = () => {
  form.value = {
    code: '',
    type: 'percent',
    value: '',
    currency: (props.currentPaywall?.currency || 'usd').toLowerCase(),
    max_redemptions: '',
    expires_at: null,
  }
}

const openForm = () => {
  resetForm()
  showForm.value = true
}

const createCode = async () => {
  const code = String(form.value.code || '').trim().toUpperCase()
  if (!code) {
    notify({ group: 'error', text: 'Enter a code' })
    return
  }
  if (form.value.type !== 'comp') {
    const n = Number(form.value.value)
    if (!n || n <= 0) {
      notify({ group: 'error', text: 'Enter a discount value greater than 0' })
      return
    }
    if (form.value.type === 'percent' && n > 100) {
      notify({ group: 'error', text: 'Percentage cannot exceed 100' })
      return
    }
  }

  const payload = {
    code,
    type: form.value.type,
    stream: props.currentPaywall.id,
    active: true,
  }
  if (form.value.type !== 'comp') payload.value = Number(form.value.value)
  if (form.value.type === 'fixed') payload.currency = String(form.value.currency || '').toLowerCase()
  if (form.value.max_redemptions) payload.max_redemptions = Number(form.value.max_redemptions)
  if (form.value.expires_at) payload.expires_at = new Date(form.value.expires_at).toISOString()

  saving.value = true
  try {
    const created = await PaywallService.createPromoCode(payload)
    if (created) codes.value.unshift(created)
    showForm.value = false
    resetForm()
    notify({ group: 'success', text: 'Promo code created' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't create promo code" })
  } finally {
    saving.value = false
  }
}

const toggleActive = async (code, value) => {
  const prev = code.active
  code.active = value
  try {
    await PaywallService.updatePromoCode(code.id, { active: value })
  } catch (err) {
    code.active = prev
    notify({ group: 'error', text: err.message || "Couldn't update promo code" })
  }
}

const deleteCode = async (code) => {
  try {
    await PaywallService.deletePromoCode(code.id)
    codes.value = _.filter(codes.value, c => c.id !== code.id)
    notify({ group: 'success', text: 'Promo code deleted' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't delete promo code" })
  }
}

const copyCode = async (code) => {
  try {
    await navigator.clipboard.writeText(code.code)
    notify({ group: 'info', text: 'Code copied' })
  } catch (e) { }
}

const discountLabel = (c) => {
  if (c.type === 'comp') return 'Complimentary'
  if (c.type === 'percent') return `${c.value}% off`
  return `${c.value} ${(c.currency || '').toUpperCase()} off`
}

const redemptionLabel = (c) => {
  const used = c.redeemed_count ?? 0
  return c.max_redemptions ? `${used} / ${c.max_redemptions} used` : `${used} used`
}

const isExpired = (c) => !!c.expires_at && moment(c.expires_at).isBefore(moment())
const isMaxed = (c) => !!c.max_redemptions && (c.redeemed_count ?? 0) >= Number(c.max_redemptions)

// A small status badge — only shown when a code isn't simply live.
const codeStatus = (c) => {
  if (!c.active) return { text: 'Disabled', cls: 'bg-surface-3 text-surface-7' }
  if (isExpired(c)) return { text: 'Expired', cls: 'bg-danger/10 text-danger' }
  if (isMaxed(c)) return { text: 'Limit reached', cls: 'bg-gold/10 text-gold' }
  return null
}

const expiryLabel = (c) => {
  if (!c.expires_at) return 'No expiry'
  const d = moment(c.expires_at).format('MMM DD, YYYY')
  return isExpired(c) ? `Expired ${d}` : `Expires ${d}`
}

// --- edit (extend expiry / change limit or value) ---
const editingId = ref(null)
const savingEdit = ref(false)
const editForm = ref({ value: '', max_redemptions: '', expires_at: null })

const openEdit = (c) => {
  expandedId.value = null
  editingId.value = c.id
  editForm.value = {
    value: c.value ?? '',
    max_redemptions: c.max_redemptions ?? '',
    expires_at: c.expires_at ? new Date(c.expires_at) : null,
  }
}

const cancelEdit = () => { editingId.value = null }

const saveEdit = async (c) => {
  const payload = {}
  if (c.type !== 'comp') {
    const n = Number(editForm.value.value)
    if (!n || n <= 0) { notify({ group: 'error', text: 'Enter a discount value greater than 0' }); return }
    if (c.type === 'percent' && n > 100) { notify({ group: 'error', text: 'Percentage cannot exceed 100' }); return }
    payload.value = n
  }
  payload.max_redemptions = editForm.value.max_redemptions ? Number(editForm.value.max_redemptions) : null
  payload.expires_at = editForm.value.expires_at ? new Date(editForm.value.expires_at).toISOString() : null

  savingEdit.value = true
  try {
    await PaywallService.updatePromoCode(c.id, payload)
    // Apply the known payload locally (avoids depending on the response shape).
    const idx = codes.value.findIndex((x) => x.id === c.id)
    if (idx !== -1) codes.value[idx] = { ...codes.value[idx], ...payload }
    editingId.value = null
    notify({ group: 'success', text: 'Promo code updated' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update promo code" })
  } finally {
    savingEdit.value = false
  }
}

const redemptionDate = (r) => r.redeemed_at ? moment(r.redeemed_at).format('MMM DD, YYYY') : ''

const redemptionsFor = (code) => redemptionsByCode.value[code.id] || []

const ucfirst = (s) => {
  s = String(s || '')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const statusClass = (status) => {
  if (status === 'active') return 'bg-success-transparent text-success'
  if (status === 'refund') return 'bg-gold/10 text-gold'
  return 'bg-surface-3 text-surface-7'
}

const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`

const exportRedemptions = (code) => {
  const rows = redemptionsFor(code)
  if (!rows.length) return
  const header = ['Code', 'Name', 'Email', 'Amount paid', 'Discount', 'Currency', 'Status', 'Redeemed at', 'Invoice']
  const body = rows.map((r) => [
    code.code,
    r.user_name || '',
    r.user_email || '',
    r.free ? 'Free' : r.amount_paid,
    r.discount_amount || '',
    r.currency || '',
    r.status || '',
    r.redeemed_at ? moment(r.redeemed_at).format('YYYY-MM-DD HH:mm') : '',
    r.invoice || '',
  ])
  const csv = [header, ...body].map((row) => row.map(csvEscape).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `promo-${code.code}-redemptions.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const toggleRedemptions = async (code) => {
  if (expandedId.value === code.id) {
    expandedId.value = null
    return
  }
  expandedId.value = code.id
  loadingRedemptions.value = code.id
  try {
    redemptionsByCode.value[code.id] = (await PaywallService.getPromoRedemptions(code.id)) || []
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't load redemptions" })
    redemptionsByCode.value[code.id] = []
  } finally {
    loadingRedemptions.value = null
  }
}
</script>

<template>
  <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
    <div class="flex items-start justify-between gap-3 mb-4">
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-white mb-0.5">Promotional codes</h3>
        <p class="text-tiny text-surface-7 mb-0">Issue discount and complimentary-access codes that your customers can
          redeem at checkout for this paywall.</p>
      </div>
      <FormButton v-if="!showForm" size="sm" label="Add code" @click="openForm" />
    </div>

    <!-- Create form -->
    <div v-if="showForm" class="rounded-lg bg-surface-2 p-4 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormInput label="Code" v-model="form.code" placeholder="SUMMER25" :maxlength="64" />
        <FormSelect label="Discount type" v-model="form.type" :options="typeOptions" />
        <FormInput v-if="form.type !== 'comp'" type="number" label="Value" v-model="form.value"
          :placeholder="form.type === 'percent' ? 'e.g. 25' : 'e.g. 5.00'" />
        <FormInput v-if="form.type === 'fixed'" label="Currency" v-model="form.currency" :maxlength="3"
          placeholder="usd" />
        <FormInput type="number" label="Max redemptions (optional)" v-model="form.max_redemptions"
          placeholder="Unlimited" />
        <div>
          <label class="block font-medium text-tiny text-surface-text-1 mb-1">Expiry (optional)</label>
          <InlineDateTimePicker v-model="form.expires_at" date-only size="sm" />
        </div>
      </div>
      <div class="flex gap-2 mt-4">
        <FormButton :label="saving ? 'Creating…' : 'Create code'" :disabled="saving" @click="createCode" />
        <FormButton type="secondary" label="Cancel" @click="showForm = false" />
      </div>
    </div>

    <!-- List -->
    <Spinner v-if="loading" classes="text-dark-8 py-6" spinner-color="var(--c-dark-8)" spinner-size="14px" />
    <div v-else-if="!codes.length" class="rounded-lg bg-surface-2 p-6 text-center">
      <p class="text-tiny text-surface-7 mb-0">No promotional codes have been created for this paywall yet.</p>
    </div>
    <ul v-else class="flex flex-col gap-2">
      <li v-for="c in codes" :key="c.id" class="rounded-lg bg-surface-2 ring-1 ring-white/[0.04]">
        <div class="flex items-center gap-3 px-4 py-3">
          <FormButton @click="copyCode(c)" title="Copy code"
            custom-classes="font-mono text-sm font-semibold text-white hover:text-main transition-colors shrink-0">{{ c.code }}</FormButton>
          <div class="min-w-0 grow">
            <div class="flex items-center gap-2 min-w-0">
              <p class="text-tiny text-surface-8 mb-0 truncate">{{ discountLabel(c) }}</p>
              <span v-if="codeStatus(c)" class="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                :class="codeStatus(c).cls">{{ codeStatus(c).text }}</span>
            </div>
            <p class="text-[11px] mb-0 truncate" :class="isExpired(c) ? 'text-danger' : 'text-surface-6'">
              {{ redemptionLabel(c) }} · {{ expiryLabel(c) }}</p>
          </div>
          <FormButton @click="toggleRedemptions(c)"
            custom-classes="shrink-0 text-tiny font-medium text-main hover:underline">
            {{ expandedId === c.id ? 'Hide' : 'View' }} redemptions
          </FormButton>
          <FormSwitch size="sm" :modelValue="!!c.active" @update:modelValue="(v) => toggleActive(c, v)" />
          <FormButton @click="editingId === c.id ? cancelEdit() : openEdit(c)" title="Edit code"
            :custom-classes="'shrink-0 transition-colors ' + (editingId === c.id ? 'text-white' : 'text-surface-7 hover:text-white')">
            <icon-base fill="none">
              <icon-edit />
            </icon-base>
          </FormButton>
          <FormButton @click="deleteCode(c)" title="Delete code"
            custom-classes="shrink-0 text-surface-7 hover:text-danger transition-colors">
            <icon-base fill="none">
              <icon-bin />
            </icon-base>
          </FormButton>
        </div>

        <!-- Edit: extend expiry / change limit or discount value -->
        <div v-if="editingId === c.id" class="border-t border-white/[0.06] px-4 py-3 bg-surface-1/40">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FormInput v-if="c.type !== 'comp'" type="number" label="Discount value" v-model="editForm.value" />
            <FormInput type="number" label="Max redemptions" v-model="editForm.max_redemptions" placeholder="Unlimited" />
            <div>
              <label class="block font-medium text-tiny text-surface-text-1 mb-1">Expiry</label>
              <InlineDateTimePicker v-model="editForm.expires_at" date-only size="sm" />
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-3">
            <FormButton size="sm" type="secondary" label="Cancel" @click="cancelEdit" />
            <FormButton size="sm" :label="savingEdit ? 'Saving…' : 'Save changes'" :disabled="savingEdit"
              @click="saveEdit(c)" />
          </div>
        </div>

        <!-- Redemptions -->
        <div v-if="expandedId === c.id" class="border-t border-white/[0.06] px-4 py-3 bg-surface-1/40">
          <Spinner v-if="loadingRedemptions === c.id" classes="text-dark-8 py-2 !justify-start"
            spinner-color="var(--c-dark-8)" spinner-size="13px" />
          <template v-else>
            <div class="flex items-center justify-between gap-2 mb-2">
              <span class="text-[11px] font-semibold uppercase tracking-wider text-surface-6">
                {{ redemptionsFor(c).length }} {{ redemptionsFor(c).length === 1 ? 'redemption' : 'redemptions' }}
              </span>
              <FormButton v-if="redemptionsFor(c).length" @click="exportRedemptions(c)"
                custom-classes="inline-flex items-center gap-1 text-tiny font-medium text-main hover:underline">
                <icon-base fill="none" width="13" height="13">
                  <icon-download />
                </icon-base>
                Export CSV
              </FormButton>
            </div>

            <p v-if="!redemptionsFor(c).length" class="text-tiny text-surface-6 mb-0">No redemptions yet.</p>

            <ul v-else class="flex flex-col divide-y divide-white/[0.05]">
              <li v-for="(r, i) in redemptionsFor(c)" :key="i" class="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                <div class="min-w-0 grow">
                  <p class="text-tiny text-surface-8 mb-0 truncate">{{ r.user_name || 'Unknown' }}</p>
                  <p class="text-[11px] text-surface-6 mb-0 truncate">{{ r.user_email }}</p>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-tiny mb-0" :class="r.free ? 'text-success' : 'text-surface-8'">
                    <span v-if="r.free">Free</span>
                    <span v-else>{{ r.amount_paid }} {{ r.currency }}</span>
                  </p>
                  <p class="text-[11px] text-surface-6 mb-0">{{ redemptionDate(r) }}</p>
                </div>
                <span v-if="r.status && r.status !== 'active'"
                  class="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded" :class="statusClass(r.status)">
                  {{ ucfirst(r.status) }}
                </span>
              </li>
            </ul>
          </template>
        </div>
      </li>
    </ul>
  </section>
</template>

<style scoped></style>
